import { keepPreviousData, skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import contactService from '../services/contact.service';
import { Contact, ContactApiParams } from '../types/contact.types';

const STALE_MS = 30_000;

export type ContactTypeFilter =
  | 'ALL' | 'INDIVIDUAL' | 'VENDOR' | 'CUSTOMER' | 'PARTNER' | 'EMPLOYEE' | 'OTHER';

export interface ContactListParams {
  search: string;
  type: ContactTypeFilter;
  activeOnly: boolean;
  page: number;
}

export function apiParamsFor(p: ContactListParams): ContactApiParams {
  const params: ContactApiParams = { page: p.page };
  if (p.search) params.search = p.search;
  if (p.type !== 'ALL') params.contact_type = p.type;
  if (p.activeOnly) params.is_active = true;
  return params;
}

export function useContactsPage(p: ContactListParams) {
  return useQuery({
    queryKey: ['contacts-page', p],
    queryFn: () => contactService.getContacts(apiParamsFor(p)),
    staleTime: STALE_MS,
    placeholderData: keepPreviousData,
  });
}

export function useContactDetail(contactId: string | null) {
  return useQuery({
    queryKey: ['contact-detail', contactId],
    queryFn: contactId == null ? skipToken : () => contactService.getContact(contactId),
    staleTime: STALE_MS,
  });
}

export function useUpdateContactInline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Contact> }) =>
      contactService.updateContact(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts-page'] });
      queryClient.invalidateQueries({ queryKey: ['contact-detail'] });
    },
  });
}

export function useDeleteContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => contactService.deleteContact(id),
    onSuccess: (_result, id) => {
      // Drop the cached detail outright — browser-back to the deleted
      // contact's URL must not re-render it from cache within staleTime.
      queryClient.removeQueries({ queryKey: ['contact-detail', id] });
      queryClient.invalidateQueries({ queryKey: ['contacts-page'] });
    },
  });
}
